import { Component, EventEmitter, OnInit, ChangeDetectionStrategy } from '@angular/core';
import {
  Vessel,
  VesselsService,
} from 'src/app/transportation/vessels/vessels.service';
import {
  OrganisationService,
} from 'src/app/organisation/organisation.service';
import { forkJoin } from 'rxjs';
import { ActivatedRoute, Router } from '@angular/router';
import {
  PgMessagesService,
  MessageLevel,
} from 'src/app/shared/services/pg-messages.service';
import {
  DocumentReference,
  DocumentService,
} from 'src/app/documents/documents.service';
import { PgTitleComponent } from 'src/app/shared/components/title-bar/title-bar.component';
import { saveAs } from 'file-saver';
import { SharedModule } from 'src/app/shared/shared.module';
import { MatIconModule } from '@angular/material/icon';
import { MaterialOrganisationSelectorComponent } from 'src/app/shared/components/material-autocomplete/material-organisationselector.component';
import { MaterialAutoCompleteComponent } from 'src/app/shared/components/material-autocomplete/material-auto-complete.component';
import { MatSelectModule } from '@angular/material/select';
import { MaterialChunkFileUploadComponent } from 'src/app/shared/components/material-file-uploader/material-chunk-file-upload.component';
import { MaterialPdfViewerComponent } from 'src/app/shared/components/material-pdf-viewer/material-pdf-viewer.component';
import { MaterialPgInputComponent } from 'src/app/shared/components/material-pg-input/material-pg-input.component';
import { MaterialPgButtonComponent } from 'src/app/shared/components/material-pg-button/material-pg-button.component';
import { Organisation } from 'src/app/generated/services/organisations/organisation';
import { MaterialOffsetDateTimeComponent } from 'src/app/shared/components/material-offset-date-time/material-offset-date-time.component';
import { OilServiceSpa } from 'src/app/generated/services/transportation/reports/oilSpa';
import { OilDeliveryNote } from 'src/app/generated/services/transportation/reports/oil';

enum OilType {
  mainEngineCylinderOil = 'Main Engine Cylinder Oil',
  mainEngineSystemOil = 'Main Engine System Oil',
  auxiliarySystemOil = 'Auxiliary System Oil',
}

@Component({
  selector: 'app-oil',
  templateUrl: './oil-component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    SharedModule,
    MatIconModule,
    MaterialOrganisationSelectorComponent,
    MaterialAutoCompleteComponent,
    MatSelectModule,
    MaterialChunkFileUploadComponent,
    MaterialPdfViewerComponent,
    MaterialPgInputComponent,
    MaterialPgButtonComponent,
    MaterialOffsetDateTimeComponent
  ]
})

export class OilDataEntryComponent implements OnInit, PgTitleComponent {
  valid: any;
  title: EventEmitter<string> = new EventEmitter();
  toolbarComponent: any;
  uploadedFileId: string | undefined;
  currentFiles: FileList | undefined;
  selectedDocumentType: string | undefined;
  vessels: Vessel[] = [];
  odnNumber!: string | null;
  timestamp!: string | null;
  ports: Organisation[] = [];
  oilDeliveryNote = OilDeliveryNote.create();
  selectedPortOrganisation: Organisation | undefined;
  locationSelected = false;
  selectedSupplierOrganisation: Organisation | undefined;
  message: string | undefined;
  supplier: Organisation[] = [];
  quantity: number | undefined;
  edit: boolean = false;
  selectedVessel: Vessel | undefined;
  date: any;
  vesselImo!: string | null;
  product!: string | null;
  expectedVersion = 0;
  productName = [
    OilType.auxiliarySystemOil,
    OilType.mainEngineCylinderOil,
    OilType.mainEngineSystemOil,
  ];
  doc?: DocumentReference;

  constructor(
    private _activateRoute: ActivatedRoute,
    private _organisationService: OrganisationService,
    private _documentsService: DocumentService,
    private _router: Router,
    private _pgMessagesService: PgMessagesService,
    private _vesselsService: VesselsService,
    private _oilService: OilServiceSpa
  ) { }

  ngOnInit() {
    this.valid = false;
    this.title.emit('Oil Delivery Note');
    this._activateRoute.paramMap.subscribe((p) => {
      this.vesselImo = p.get('vesselImo');
      this.odnNumber = p.get('odnNumber');
      this.product = p.get('product');
      this.timestamp = p.get('date');
      this.oilDeliveryNote = OilDeliveryNote.create();
      this.edit = this.vesselImo ? true : false;
    });
    forkJoin([
      this._organisationService.getPorts(),
      this._organisationService.getOilSupplier(),
      this._vesselsService.getVessels(),
    ]).subscribe((results) => {
      this.ports = results[0];
      this.supplier = results[1];
      this.vessels = results[2].filter((v) => v.active);
      this.initOilDeliveryNote();
    });
  }

  async initOilDeliveryNote() {
    if (this.vesselImo && this.product && this.timestamp &&  this.odnNumber) {
      this.edit = true;
      await this._oilService
        .getOneOilDeliveryNote({
          vesselImo: this.vesselImo,
          odnNumber: this.odnNumber,
          product: this.product,
          timestamp: this.timestamp})
        .then(value => {
          this.oilDeliveryNote = value.data ?? OilDeliveryNote.create();
          this.expectedVersion = value.version;
        });
      this.selectedVessel = this.vessels.find((v) => v.imo === this.oilDeliveryNote?.vesselImo);
      this.selectedPortOrganisation = this.ports.find(
        (p) => p.name === this.oilDeliveryNote?.locationOrganisationId
      );
      this.selectedSupplierOrganisation = this.supplier.find(
        (p) => p.name === this.oilDeliveryNote?.supplierOrganisationId
      );
      this.valid = this.oilDeliveryNote?.fileId ? true : false;
      this.uploadedFileId = this.oilDeliveryNote?.fileId;
    }
  }

  uploadODNFile(odn: DocumentReference) {
    this.oilDeliveryNote.fileId = odn.id;
  }

  save() {
    this.oilDeliveryNote.vesselImo = this.selectedVessel!.imo;
    this.oilDeliveryNote.vesselName = this.selectedVessel!.name!.name;
    this.oilDeliveryNote.supplierOrganisationId = this.selectedSupplierOrganisation!.name;
    this.oilDeliveryNote.locationOrganisationId = this.selectedPortOrganisation!.name;
    if (this.edit) {
      this._oilService.updateOilDeliveryNote({data: this.oilDeliveryNote, expectedVersion: this.expectedVersion })
        .then(() => {
          this.message = `${this.oilDeliveryNote.vesselName}`;
          this._pgMessagesService.publishMessage({
            message: this.message,
            topic: 'Submitting Oil Delivery Note successfully',
            level: MessageLevel.Info,
          });
          this._router
            .navigateByUrl('/', { skipLocationChange: true })
            .then(() => {
              this._router
                .navigate([
                  'vessels/oil',
                  this.oilDeliveryNote.vesselImo,
                  this.oilDeliveryNote.bdnNumber,
                  this.oilDeliveryNote.product,
                  this.oilDeliveryNote.date?.isoDate,
                ])
                .then((e) => console.log(`Result of Navigation is ${e}`));
            });
        },
        );
    } else {
      this._oilService.addOilDeliveryNote(this.oilDeliveryNote).then(() => {
        this.message = `${this.oilDeliveryNote.vesselName}`;
        this._pgMessagesService.publishMessage({
          message: this.message,
          topic: 'Submitting Oil Delivery Note successfully',
          level: MessageLevel.Info,
        });
        this._router
          .navigateByUrl('/', { skipLocationChange: true })
          .then(() => {
            this._router
              .navigate([
                'vessels/oil',
                this.oilDeliveryNote.vesselImo,
                this.oilDeliveryNote.bdnNumber,
                this.oilDeliveryNote.product,
                this.oilDeliveryNote.date?.isoDate,
              ])
              .then((e) => console.log(`Result of Navigation is ${e}`));
          });
      }
      );
    }
  }

  remove() {
    if (confirm('Are you sure you want to delete this Oil Delivery Note?')) {
      this.oilDeliveryNote.isDeleted = true;
      this._oilService.updateOilDeliveryNote({data: this.oilDeliveryNote, expectedVersion: this.expectedVersion}).then(() => {
        this._router.navigate(['vessels/oil']);
        this.message = 'Remove Oil Delivery Note';
        this._pgMessagesService.publishMessage({
          message: this.message,
          topic: 'Deleted',
          level: MessageLevel.Info,
        });
      });
    }
  }

  downloadFile(fileId: string) {
    this._documentsService.download(fileId).subscribe(
      (b) => {
        saveAs(b.body, `${this.selectedSupplierOrganisation?.name}.pdf`);
      },
      () => {
        this._pgMessagesService.publishMessage({
          message: 'Could not download this file.',
          level: MessageLevel.Error,
          topic: 'oil document',
        });
      }
    );
  }

  vesselFormatter(vessel: Vessel): string {
    return vessel?.name?.name ?? '';
  }

  productFormatter(product: string): string {
    return product ?? '';
  }

}
